import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import GlassCard from '../components/GlassCard';
import SearchablePersonSelect from '../components/SearchablePersonSelect';
import { peopleAPI, relationshipsAPI, familiesAPI, hobbiesAPI, socialLinksAPI, contactsAPI } from '../services/api';
import { formatDateForInput, formatDateForDisplay, formatDateShort } from '../utils/dateUtils';

const PersonDetailPage = () => {
  const { id } = useParams();
  const { theme } = useTheme();
  const { user } = useAuth();
  
  // Refs for relationship forms to enable scrolling
  const addParentFormRef = useRef(null);
  const addChildFormRef = useRef(null);
  const addPartnerFormRef = useRef(null);
  const addSiblingFormRef = useRef(null);
  const [person, setPerson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [familyId, setFamilyId] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [showPictureEdit, setShowPictureEdit] = useState(false);
  const [pictureUrl, setPictureUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showHobbiesForm, setShowHobbiesForm] = useState(false);
  const [showSocialLinkForm, setShowSocialLinkForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [newHobby, setNewHobby] = useState('');
  const [newSocialLink, setNewSocialLink] = useState({ platform: 'facebook', url: '' });
  const [newContact, setNewContact] = useState({ contact_type: 'phone', value: '', is_primary: false });
  const [editingHobby, setEditingHobby] = useState(null); // { index, originalText }
  const [editingContact, setEditingContact] = useState(null); // contact object
  const [editingSocialLink, setEditingSocialLink] = useState(null); // social link object
  const [editHobbyText, setEditHobbyText] = useState('');
  const [editContactData, setEditContactData] = useState({ contact_type: 'phone', value: '', is_primary: false });
  const [editSocialLinkData, setEditSocialLinkData] = useState({ platform: 'facebook', url: '' });
  const [activeEditTab, setActiveEditTab] = useState('basic'); // 'basic', 'hobbies', 'contacts', 'social'
  const [editingRelationship, setEditingRelationship] = useState(null); // { type: 'parent'|'child'|'spouse', personId, relationshipId, ... }
  const [showAddParentForm, setShowAddParentForm] = useState(false);
  const [showAddChildForm, setShowAddChildForm] = useState(false);
  const [showAddPartnerForm, setShowAddPartnerForm] = useState(false);
  const [showAddSiblingForm, setShowAddSiblingForm] = useState(false);
  const [linkData, setLinkData] = useState({
    related_person_id: '',
    relationship_type: '', // 'parent', 'child', 'sibling', 'partner'
    relationship_role: '',
    spouse_role: '', // 'husband' or 'wife'
    sibling_role: '', // 'brother' or 'sister'
    child_role: '', // 'son' or 'daughter'
    new_person: {
      first_name: '',
      last_name: '',
      preferred_name: '',
      date_of_birth: '',
    },
    link_existing: true, // true = link existing, false = create new
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    preferred_name: '',
    sex: 'unknown',
    date_of_birth: '',
    date_of_death: '',
    city: '',
    country: '',
    notes: '',
  });

  useEffect(() => {
    loadPerson();
  }, [id]);

  // Auto-scroll to forms when they are shown
  useEffect(() => {
    if (showAddParentForm && addParentFormRef.current) {
      setTimeout(() => {
        const element = addParentFormRef.current;
        if (element) {
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - 80; // 80px offset for mobile header
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 150);
    }
  }, [showAddParentForm]);

  useEffect(() => {
    if (showAddChildForm && addChildFormRef.current) {
      setTimeout(() => {
        const element = addChildFormRef.current;
        if (element) {
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - 80; // 80px offset for mobile header
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 150);
    }
  }, [showAddChildForm]);

  useEffect(() => {
    if (showAddPartnerForm && addPartnerFormRef.current) {
      setTimeout(() => {
        const element = addPartnerFormRef.current;
        if (element) {
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - 80; // 80px offset for mobile header
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 150);
    }
  }, [showAddPartnerForm]);

  useEffect(() => {
    if (showAddSiblingForm && addSiblingFormRef.current) {
      setTimeout(() => {
        const element = addSiblingFormRef.current;
        if (element) {
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - 80; // 80px offset for mobile header
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 150);
    }
  }, [showAddSiblingForm]);

  useEffect(() => {
    if (person) {
      setEditFormData({
        first_name: person.first_name || '',
        middle_name: person.middle_name || '',
        last_name: person.last_name || '',
        preferred_name: person.preferred_name || '',
        sex: person.sex || 'unknown',
        date_of_birth: formatDateForInput(person.date_of_birth),
        date_of_death: formatDateForInput(person.date_of_death),
        city: person.city || '',
        country: person.country || '',
        notes: person.notes || '',
      });
    }
  }, [person]);


  const loadPerson = async () => {
    try {
      setLoading(true);
      const response = await peopleAPI.get(id);
      setPerson(response.data);
      setFamilyId(response.data.family_id);
      // Load family members for relationship linking
      if (response.data.family_id) {
        const familyResponse = await peopleAPI.list(response.data.family_id);
        setFamilyMembers(familyResponse.data.people || []);
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load person');
    } finally {
      setLoading(false);
    }
  };

  const handleAddParent = async (e) => {
    e.preventDefault();
    try {
      let parentId = linkData.related_person_id;
      
      // If creating new parent
      if (!linkData.link_existing) {
        if (!linkData.new_person.first_name.trim()) {
          setError('First name is required');
          return;
        }
        const personResponse = await peopleAPI.create(familyId, {
          first_name: linkData.new_person.first_name,
          last_name: linkData.new_person.last_name,
          preferred_name: linkData.new_person.preferred_name,
          date_of_birth: linkData.new_person.date_of_birth || null,
        });
        parentId = personResponse.data.id;
      } else {
        if (!parentId) {
          setError('Please select a parent');
          return;
        }
      }

      // Create parent-child relationship (parent is parent, current person is child)
      await relationshipsAPI.parentChild.create(familyId, {
        parent_person_id: parentId,
        child_person_id: id,
        kind: 'biological',
      });

      setShowAddParentForm(false);
      setLinkData({ related_person_id: '', relationship_type: '', relationship_role: '', spouse_role: '', sibling_role: '', child_role: '', new_person: { first_name: '', last_name: '', preferred_name: '', date_of_birth: '' }, link_existing: true });
      setError(null);
      await loadPerson();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add parent');
    }
  };

  const handleAddChild = async (e) => {
    e.preventDefault();
    try {
      if (!linkData.child_role) {
        setError('Please select whether this is a Son or Daughter');
        return;
      }

      let childId = linkData.related_person_id;
      
      // If creating new child
      if (!linkData.link_existing) {
        if (!linkData.new_person.first_name.trim()) {
          setError('First name is required');
          return;
        }
        
        // Determine gender based on child role
        let childSex = 'unknown';
        if (linkData.child_role === 'son') {
          childSex = 'male';
        } else if (linkData.child_role === 'daughter') {
          childSex = 'female';
        }
        
        const personResponse = await peopleAPI.create(familyId, {
          first_name: linkData.new_person.first_name,
          last_name: linkData.new_person.last_name,
          preferred_name: linkData.new_person.preferred_name,
          date_of_birth: linkData.new_person.date_of_birth || null,
          sex: childSex,
        });
        childId = personResponse.data.id;
      } else {
        if (!childId) {
          setError('Please select a child');
          return;
        }
      }

      // Create parent-child relationship (current person is parent, child is child)
      await relationshipsAPI.parentChild.create(familyId, {
        parent_person_id: id,
        child_person_id: childId,
        kind: 'biological',
      });

      setShowAddChildForm(false);
      setLinkData({ related_person_id: '', relationship_type: '', relationship_role: '', spouse_role: '', sibling_role: '', child_role: '', new_person: { first_name: '', last_name: '', preferred_name: '', date_of_birth: '' }, link_existing: true });
      setError(null);
      await loadPerson();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add child');
    }
  };

  const handleAddPartner = async (e) => {
    e.preventDefault();
    try {
      if (!linkData.spouse_role) {
        setError('Please select whether this is a Husband or Wife');
        return;
      }

      let partnerId = linkData.related_person_id;
      
      // If creating new spouse
      if (!linkData.link_existing) {
        if (!linkData.new_person.first_name.trim()) {
          setError('First name is required');
          return;
        }
        
        // Determine gender based on spouse role
        // If current person is male, spouse_role 'wife' means female, 'husband' means male
        // If current person is female, spouse_role 'husband' means male, 'wife' means female
        const currentPersonSex = person.sex || 'unknown';
        let spouseSex = 'unknown';
        
        if (linkData.spouse_role === 'husband') {
          spouseSex = 'male';
        } else if (linkData.spouse_role === 'wife') {
          spouseSex = 'female';
        }
        
        const personResponse = await peopleAPI.create(familyId, {
          first_name: linkData.new_person.first_name,
          last_name: linkData.new_person.last_name,
          preferred_name: linkData.new_person.preferred_name,
          date_of_birth: linkData.new_person.date_of_birth || null,
          sex: spouseSex,
        });
        partnerId = personResponse.data.id;
      } else {
        if (!partnerId) {
          setError('Please select a spouse');
          return;
        }
      }

      // Create partnership
      await relationshipsAPI.partnerships.create(familyId, {
        person_id_1: id,
        person_id_2: partnerId,
        partnership_type: 'spouse',
      });

      setShowAddPartnerForm(false);
      setLinkData({ related_person_id: '', relationship_type: '', relationship_role: '', spouse_role: '', sibling_role: '', new_person: { first_name: '', last_name: '', preferred_name: '', date_of_birth: '' }, link_existing: true });
      setError(null);
      await loadPerson();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add spouse');
    }
  };

  const handleAddSibling = async (e) => {
    e.preventDefault();
    try {
      if (!linkData.sibling_role) {
        setError('Please select whether this is a Brother or Sister');
        return;
      }

      // Get current person's parents
      const currentPersonParents = person.parents || [];
      let siblingId = linkData.related_person_id;
      
      // If creating new sibling
      if (!linkData.link_existing) {
        if (!linkData.new_person.first_name.trim()) {
          setError('First name is required');
          return;
        }
        
        // Determine gender based on sibling role
        let siblingSex = 'unknown';
        if (linkData.sibling_role === 'brother') {
          siblingSex = 'male';
        } else if (linkData.sibling_role === 'sister') {
          siblingSex = 'female';
        }
        
        const personResponse = await peopleAPI.create(familyId, {
          first_name: linkData.new_person.first_name,
          last_name: linkData.new_person.last_name,
          preferred_name: linkData.new_person.preferred_name,
          date_of_birth: linkData.new_person.date_of_birth || null,
          sex: siblingSex,
        });
        siblingId = personResponse.data.id;
        
        // If creating new sibling and current person has no parents, 
        // we can't establish sibling relationship yet (requires shared parents)
        // But we still allow creating the person - they can add shared parents later
        if (currentPersonParents.length === 0) {
          setShowAddSiblingForm(false);
          setLinkData({ related_person_id: '', relationship_type: '', relationship_role: '', spouse_role: '', sibling_role: '', child_role: '', new_person: { first_name: '', last_name: '', preferred_name: '', date_of_birth: '' }, link_existing: true });
          setError(null);
          await loadPerson();
          return; // Exit early - sibling person created but relationship not established
        }
      } else {
        // Linking existing sibling
        if (!siblingId) {
          setError('Please select a sibling');
          return;
        }
        
        // If current person has no parents, check if sibling has parents
        // If sibling has parents, add current person as child of those parents
        if (currentPersonParents.length === 0) {
          try {
            const siblingResponse = await peopleAPI.getWithRelationships(siblingId);
            const siblingParents = siblingResponse.data.parents || [];
            
            if (siblingParents.length > 0) {
              // Add current person as child of sibling's parents
              for (const parent of siblingParents) {
                try {
                  await relationshipsAPI.parentChild.create(familyId, {
                    parent_person_id: parent.id,
                    child_person_id: id,
                    kind: 'biological',
                  });
                } catch (err) {
                  // Ignore if relationship already exists
                  if (err.response?.status !== 400) {
                    throw err;
                  }
                }
              }
              // Now both are children of the same parents, so they're siblings
              setShowAddSiblingForm(false);
              setLinkData({ related_person_id: '', relationship_type: '', relationship_role: '', spouse_role: '', sibling_role: '', child_role: '', new_person: { first_name: '', last_name: '', preferred_name: '', date_of_birth: '' }, link_existing: true });
              setError(null);
              await loadPerson();
              return;
            } else {
              // Neither has parents - can't establish sibling relationship yet
              // But we'll allow the operation and inform the user
              setError('Neither person has parents. To establish a sibling relationship, you need to add shared parents for both people.');
              return;
            }
          } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch sibling information');
            return;
          }
        }
      }

      // If we reach here, current person has parents - add sibling as child of those parents
      for (const parent of currentPersonParents) {
        try {
          await relationshipsAPI.parentChild.create(familyId, {
            parent_person_id: parent.id,
            child_person_id: siblingId,
            kind: 'biological',
          });
        } catch (err) {
          // Ignore if relationship already exists
          if (err.response?.status !== 400) {
            throw err;
          }
        }
      }

      setShowAddSiblingForm(false);
      setLinkData({ related_person_id: '', relationship_type: '', relationship_role: '', spouse_role: '', sibling_role: '', child_role: '', new_person: { first_name: '', last_name: '', preferred_name: '', date_of_birth: '' }, link_existing: true });
      setError(null);
      await loadPerson();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add sibling');
    }
  };

  const handleUpdatePicture = async () => {
    try {
      await peopleAPI.update(id, { picture_url: pictureUrl });
      setShowPictureEdit(false);
      setPictureUrl('');
      setSelectedFile(null);
      setPreviewUrl(null);
      await loadPerson();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update picture');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please select a valid image file (PNG, JPEG, GIF, or WebP)');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Image file size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setError('');
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadPicture = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }
    
    try {
      setUploading(true);
      setError('');
      const response = await peopleAPI.uploadPicture(id, selectedFile);
      setShowPictureEdit(false);
      setPictureUrl('');
      setSelectedFile(null);
      setPreviewUrl(null);
      await loadPerson();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload picture');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      // Only save basic info if we're on the basic tab
      if (activeEditTab === 'basic') {
        const updateData = {};
        Object.keys(editFormData).forEach(key => {
          if (editFormData[key] !== (person[key] || '')) {
            if (key === 'date_of_birth' || key === 'date_of_death') {
              updateData[key] = editFormData[key] || null;
            } else {
              updateData[key] = editFormData[key] || null;
            }
          }
        });
        if (Object.keys(updateData).length > 0) {
          await peopleAPI.update(id, updateData);
        }
      }
      // For other tabs, the individual handlers (handleAddHobby, handleAddContact, etc.) handle the saves
      // So we just close the edit mode if on basic tab
      if (activeEditTab === 'basic') {
        setIsEditing(false);
        setActiveEditTab('basic');
        await loadPerson();
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update member');
    }
  };

  const handleClaimMember = async () => {
    try {
      await peopleAPI.claim(id);
      await loadPerson();
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to claim member');
    }
  };

  const handleAddHobby = async (e) => {
    e.preventDefault();
    if (!newHobby.trim()) return;
    try {
      // Create hobby as freeform text - we'll store it in a simple text field
      // For now, we'll use the notes field or create a new endpoint
      // Let's create a simple text-based hobby storage
      const currentHobbies = person.hobbies_text || '';
      const updatedHobbies = currentHobbies ? `${currentHobbies}, ${newHobby.trim()}` : newHobby.trim();
      await peopleAPI.update(id, { hobbies_text: updatedHobbies });
      setNewHobby('');
      setShowHobbiesForm(false);
      await loadPerson();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add hobby');
    }
  };

  const handleRemoveHobby = async (hobbyText) => {
    try {
      const currentHobbies = (person.hobbies_text || '').split(',').map(h => h.trim()).filter(h => h && h !== hobbyText);
      await peopleAPI.update(id, { hobbies_text: currentHobbies.join(', ') });
      await loadPerson();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove hobby');
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    if (!newContact.value.trim()) return;
    try {
      await contactsAPI.create(id, newContact);
      setNewContact({ contact_type: 'phone', value: '', is_primary: false });
      setShowContactForm(false);
      await loadPerson();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await contactsAPI.delete(contactId);
      await loadPerson();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete contact');
    }
  };

  const handleAddSocialLink = async (e) => {
    e.preventDefault();
    try {
      await socialLinksAPI.create(id, newSocialLink);
      setNewSocialLink({ platform: 'facebook', url: '' });
      setShowSocialLinkForm(false);
      await loadPerson();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add social link');
    }
  };

  const handleDeleteSocialLink = async (linkId) => {
    try {
      await socialLinksAPI.delete(linkId);
      await loadPerson();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete social link');
    }
  };

  const handleUpdateHobby = async (e, originalText) => {
    e.preventDefault();
    if (!editHobbyText.trim()) return;
    try {
      const currentHobbies = (person.hobbies_text || '').split(',').map(h => h.trim());
      const updatedHobbies = currentHobbies.map(h => h === originalText ? editHobbyText.trim() : h);
      await peopleAPI.update(id, { hobbies_text: updatedHobbies.join(', ') });
      setEditingHobby(null);
      setEditHobbyText('');
      await loadPerson();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update hobby');
    }
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();
    if (!editContactData.value.trim()) return;
    try {
      await contactsAPI.update(editingContact.id, editContactData);
      setEditingContact(null);
      setEditContactData({ contact_type: 'phone', value: '', is_primary: false });
      await loadPerson();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update contact');
    }
  };

  const handleUpdateSocialLink = async (e) => {
    e.preventDefault();
    if (!editSocialLinkData.url.trim()) return;
    try {
      await socialLinksAPI.update(editingSocialLink.id, editSocialLinkData);
      setEditingSocialLink(null);
      setEditSocialLinkData({ platform: 'facebook', url: '' });
      await loadPerson();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update social link');
    }
  };

  const handleDeleteRelationship = async (relationshipType, relationshipData) => {
    if (!window.confirm('Are you sure you want to delete this relationship?')) {
      return;
    }
    try {
      if (relationshipType === 'parent' || relationshipType === 'child') {
        // Delete parent-child relationship
        await relationshipsAPI.parentChild.delete(
          familyId,
          relationshipData.relationship_parent_id,
          relationshipData.relationship_child_id
        );
      } else if (relationshipType === 'spouse') {
        // Delete partnership
        await relationshipsAPI.partnerships.delete(relationshipData.partnership_id);
      }
      await loadPerson();
      setEditingRelationship(null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete relationship');
    }
  };

  const handleUpdateRelationship = async (relationshipType, relationshipData, updates) => {
    try {
      if (relationshipType === 'parent' || relationshipType === 'child') {
        // For parent-child, we need to delete and recreate with new kind
        await relationshipsAPI.parentChild.delete(
          familyId,
          relationshipData.relationship_parent_id,
          relationshipData.relationship_child_id
        );
        await relationshipsAPI.parentChild.create(familyId, {
          parent_person_id: relationshipData.relationship_parent_id,
          child_person_id: relationshipData.relationship_child_id,
          kind: updates.kind || 'biological',
        });
      } else if (relationshipType === 'spouse') {
        // Update partnership
        await relationshipsAPI.partnerships.update(relationshipData.partnership_id, {
          partnership_type: updates.partnership_type || 'spouse',
        });
      }
      await loadPerson();
      setEditingRelationship(null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update relationship');
    }
  };

  const isClaimedByCurrentUser = person?.claimed_by_user_id === user?.user?.id || person?.claimed_by_user_id === user?.id;
  // With simplified RLS, any family member can edit. If user is logged in and person exists (meaning they can view it), they can edit.
  // RLS will enforce the actual permissions on the backend.
  const canEdit = user && person; // Allow all logged-in users who can view the person - RLS enforces permissions
  const isAdmin = user?.user?.is_admin || user?.is_admin;

  if (loading) {
    return (
      <GlassCard style={{ textAlign: 'center', padding: '48px' }}>
        <p style={{ color: theme.text.secondary }}>Loading...</p>
      </GlassCard>
    );
  }

  if (error) {
    return (
      <GlassCard style={{ background: 'rgba(255, 0, 0, 0.1)' }}>
        <p style={{ color: theme.text.primary }}>Error: {error}</p>
        <Link
          to="/people"
          style={{
            color: theme.accent.primary,
            textDecoration: 'none',
            marginTop: '16px',
            display: 'inline-block',
          }}
        >
          ← Back to People
        </Link>
      </GlassCard>
    );
  }

  if (!person) {
    return null;
  }

  return (
    <div style={{ width: '100%', maxWidth: '100%' }} className="person-detail-page">
      <Link
        to="/people"
        style={{
          color: theme.text.secondary,
          textDecoration: 'none',
          marginBottom: '16px',
          display: 'inline-block',
          fontSize: 'clamp(14px, 2vw, 16px)',
          padding: '8px 0',
          minHeight: '44px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        ← Back to People
      </Link>

      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: '16px', 
        marginBottom: '16px', 
        alignItems: 'stretch',
      }}
      className="person-main-layout"
      >
        <GlassCard style={{ position: 'relative', width: '100%', maxWidth: '300px', margin: '0 auto' }}>
          {person.picture_url ? (
            <img
              src={person.picture_url}
              alt={`${person.first_name} ${person.last_name || ''}`}
              style={{
                width: '100%',
                borderRadius: '8px',
              }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                aspectRatio: '1',
                background: theme.card.background,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '48px',
              }}
            >
              👤
            </div>
          )}
          {canEdit && (
            <button
                  onClick={() => {
                    setShowPictureEdit(!showPictureEdit);
                    setPictureUrl(person.picture_url || '');
                  }}
              style={{
                position: 'absolute',
                bottom: '12px',
                right: '12px',
                padding: '12px 16px',
                borderRadius: '6px',
                border: 'none',
                background: theme.accent.primary,
                color: 'white',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                minHeight: '44px',
                minWidth: '44px',
              }}
            >
              {person.picture_url ? 'Edit' : 'Add'} Picture
            </button>
          )}
          {showPictureEdit && (
            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: theme.glass.border }}>
              {/* File Upload Option */}
              <div style={{ marginBottom: '16px' }}>
                <label
                  htmlFor="picture-upload"
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: theme.text.primary,
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  Upload Photo (PNG, JPEG, GIF, WebP - max 10MB)
                </label>
                <input
                  type="file"
                  id="picture-upload"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                  onChange={handleFileSelect}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '16px',
                    marginBottom: '8px',
                    minHeight: '44px',
                  }}
                />
                {previewUrl && (
                  <div style={{ marginTop: '12px', marginBottom: '12px' }}>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        borderRadius: '8px',
                        border: theme.glass.border,
                      }}
                    />
                    {selectedFile && (
                      <p style={{ color: theme.text.secondary, fontSize: '12px', marginTop: '4px' }}>
                        Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </p>
                    )}
                  </div>
                )}
                {selectedFile && (
                  <button
                    onClick={handleUploadPicture}
                    disabled={uploading}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      background: uploading ? theme.text.muted : theme.accent.primary,
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: 600,
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      minHeight: '44px',
                      marginBottom: '12px',
                    }}
                  >
                    {uploading ? 'Uploading...' : 'Upload Photo'}
                  </button>
                )}
              </div>

              {/* Divider */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                margin: '16px 0',
                color: theme.text.secondary,
              }}>
                <div style={{ flex: 1, height: '1px', background: theme.glass.border }}></div>
                <span style={{ fontSize: '12px' }}>OR</span>
                <div style={{ flex: 1, height: '1px', background: theme.glass.border }}></div>
              </div>

              {/* URL Input Option (legacy support) */}
              <div>
                <label
                  htmlFor="picture-url"
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: theme.text.primary,
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  Enter Picture URL
                </label>
                <input
                  type="text"
                  id="picture-url"
                  placeholder="https://example.com/image.jpg"
                  value={pictureUrl}
                  onChange={(e) => setPictureUrl(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '16px',
                    marginBottom: '12px',
                    minHeight: '44px',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {pictureUrl && (
                  <button
                    onClick={handleUpdatePicture}
                    disabled={uploading}
                    style={{
                      padding: '12px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      background: uploading ? theme.text.muted : theme.accent.primary,
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: 600,
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      minHeight: '44px',
                      flex: 1,
                      minWidth: '120px',
                    }}
                  >
                    Save URL
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowPictureEdit(false);
                    setPictureUrl('');
                    setSelectedFile(null);
                    setPreviewUrl(null);
                    setError('');
                  }}
                  disabled={uploading}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: 'transparent',
                    color: theme.text.primary,
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    minHeight: '44px',
                    flex: 1,
                    minWidth: '120px',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </GlassCard>

        <GlassCard style={{ flex: 1, width: '100%' }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '12px',
            justifyContent: 'space-between', 
            alignItems: 'stretch', 
            marginBottom: '16px' 
          }}
          className="person-header"
          >
            <h1 style={{ 
              fontSize: 'clamp(24px, 5vw, 36px)', 
              color: theme.text.primary, 
              margin: 0,
              wordBreak: 'break-word',
            }}>
              {person.preferred_name || person.first_name} {person.last_name || ''}
            </h1>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              gap: '8px', 
              alignItems: 'center' 
            }}
            className="person-actions"
            >
              {user && !person.claimed_by_user_id && (
                <button
                  onClick={handleClaimMember}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.accent.primary,
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                  className="claim-button"
                  title="Claim This Member"
                >
                  <span className="claim-button-text">Claim This Member</span>
                  <span className="claim-button-icon" style={{ display: 'none' }}>👤</span>
                </button>
              )}
              {isClaimedByCurrentUser && (
                <span style={{ 
                  padding: '8px 16px', 
                  borderRadius: '6px', 
                  background: 'rgba(0, 255, 0, 0.2)', 
                  color: theme.text.primary, 
                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}>
                  <span>✓</span>
                  <span className="claimed-text">Claimed by You</span>
                </span>
              )}
              {canEdit && (
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.accent.primary,
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                  className="edit-button"
                  title={isEditing ? "Cancel Edit" : "Edit"}
                >
                  <span className="edit-button-text">✏️ Edit</span>
                  <span className="edit-button-icon" style={{ display: 'none' }}>✏️</span>
                </button>
              )}
            </div>
          </div>

              {!isEditing && (
            <>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', 
                gap: '16px', 
                marginBottom: '16px' 
              }}
              className="person-info-grid"
              >
                {person.middle_name && (
                  <div>
                    <span style={{ color: theme.text.secondary, fontSize: '14px' }}>Middle Name:</span>
                    <p style={{ color: theme.text.primary, margin: '4px 0 0 0', fontSize: '16px' }}>{person.middle_name}</p>
                  </div>
                )}
                {person.sex && person.sex !== 'unknown' && (
                  <div>
                    <span style={{ color: theme.text.secondary, fontSize: '14px' }}>Gender:</span>
                    <p style={{ color: theme.text.primary, margin: '4px 0 0 0', fontSize: '16px', textTransform: 'capitalize' }}>
                      {person.sex === 'male' ? '👨 Male' : person.sex === 'female' ? '👩 Female' : person.sex}
                    </p>
                  </div>
                )}
                {person.date_of_birth && (
                  <div>
                    <span style={{ color: theme.text.secondary, fontSize: '14px' }}>Date of Birth:</span>
                    <p style={{ color: theme.text.primary, margin: '4px 0 0 0', fontSize: '16px' }}>
                      {formatDateForDisplay(person.date_of_birth)}
                    </p>
                  </div>
                )}
                {person.date_of_death && (
                  <div>
                    <span style={{ color: theme.text.secondary, fontSize: '14px' }}>Date of Death:</span>
                    <p style={{ color: theme.text.primary, margin: '4px 0 0 0', fontSize: '16px' }}>
                      {formatDateForDisplay(person.date_of_death)}
                    </p>
                  </div>
                )}
                {(person.city || person.country) && (
                  <div>
                    <span style={{ color: theme.text.secondary, fontSize: '14px' }}>Location:</span>
                    <p style={{ color: theme.text.primary, margin: '4px 0 0 0', fontSize: '16px' }}>
                      {[person.city, person.country].filter(Boolean).join(', ') || '—'}
                    </p>
                  </div>
                )}
              </div>
              {person.notes && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: theme.glass.border }}>
                  <h3 style={{ fontSize: '18px', marginBottom: '8px', color: theme.text.primary }}>
                    Notes
                  </h3>
                  <p style={{ color: theme.text.secondary, whiteSpace: 'pre-wrap' }}>{person.notes}</p>
                </div>
              )}
            </>
          )}
        </GlassCard>
      </div>

      {/* Edit Modal */}
      {isEditing && (
        <div
          className="edit-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px',
            overflowY: 'auto',
          }}
          onClick={() => {
            setIsEditing(false);
            if (person) {
              setEditFormData({
                first_name: person.first_name || '',
                middle_name: person.middle_name || '',
                last_name: person.last_name || '',
                preferred_name: person.preferred_name || '',
                sex: person.sex || 'unknown',
                date_of_birth: formatDateForInput(person.date_of_birth),
                date_of_death: formatDateForInput(person.date_of_death),
                city: person.city || '',
                country: person.country || '',
                notes: person.notes || '',
              });
            }
          }}
        >
          <GlassCard
            style={{
              maxWidth: '800px',
              width: '100%',
              maxHeight: '95vh',
              overflowY: 'auto',
              margin: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: '12px',
              justifyContent: 'space-between', 
              alignItems: 'stretch', 
              marginBottom: '20px' 
            }}
            className="edit-header"
            >
              <h2 style={{ fontSize: 'clamp(20px, 4vw, 24px)', color: theme.text.primary, margin: 0 }}>Edit Member</h2>
              <button
                onClick={() => {
                  setIsEditing(false);
                  if (person) {
                    setEditFormData({
                      first_name: person.first_name || '',
                      middle_name: person.middle_name || '',
                      last_name: person.last_name || '',
                      preferred_name: person.preferred_name || '',
                      sex: person.sex || 'unknown',
                      date_of_birth: formatDateForInput(person.date_of_birth),
                      date_of_death: formatDateForInput(person.date_of_death),
                      city: person.city || '',
                      country: person.country || '',
                      notes: person.notes || '',
                    });
                  }
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  color: theme.text.secondary,
                  cursor: 'pointer',
                  padding: 0,
                  width: '32px',
                  height: '32px',
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSaveEdit}>
              {activeEditTab === 'basic' && (
              <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', color: theme.text.secondary, fontSize: '14px' }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={editFormData.first_name}
                    onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '6px',
                      border: theme.glass.border,
                      background: theme.card.background,
                      color: theme.text.primary,
                      fontSize: '14px',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', color: theme.text.secondary, fontSize: '14px' }}>
                    Middle Name
                  </label>
                  <input
                    type="text"
                    value={editFormData.middle_name}
                    onChange={(e) => setEditFormData({ ...editFormData, middle_name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '6px',
                      border: theme.glass.border,
                      background: theme.card.background,
                      color: theme.text.primary,
                      fontSize: '14px',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', color: theme.text.secondary, fontSize: '14px' }}>
                    Surname (Maiden)
                  </label>
                  <input
                    type="text"
                    value={editFormData.last_name}
                    onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '6px',
                      border: theme.glass.border,
                      background: theme.card.background,
                      color: theme.text.primary,
                      fontSize: '14px',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', color: theme.text.secondary, fontSize: '14px' }}>
                    Preferred Name
                  </label>
                  <input
                    type="text"
                    value={editFormData.preferred_name}
                    onChange={(e) => setEditFormData({ ...editFormData, preferred_name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '6px',
                      border: theme.glass.border,
                      background: theme.card.background,
                      color: theme.text.primary,
                      fontSize: '14px',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', color: theme.text.secondary, fontSize: '14px' }}>
                    Gender *
                  </label>
                  <select
                    value={editFormData.sex}
                    onChange={(e) => setEditFormData({ ...editFormData, sex: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '6px',
                      border: theme.glass.border,
                      background: theme.card.background,
                      color: theme.text.primary,
                      fontSize: '14px',
                    }}
                  >
                    <option value="unknown">Unknown</option>
                    <option value="male">👨 Male</option>
                    <option value="female">👩 Female</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', color: theme.text.secondary, fontSize: '14px' }}>
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={editFormData.date_of_birth}
                    onChange={(e) => setEditFormData({ ...editFormData, date_of_birth: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '6px',
                      border: theme.glass.border,
                      background: theme.card.background,
                      color: theme.text.primary,
                      fontSize: '14px',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', color: theme.text.secondary, fontSize: '14px' }}>
                    Date of Death
                  </label>
                  <input
                    type="date"
                    value={editFormData.date_of_death}
                    onChange={(e) => setEditFormData({ ...editFormData, date_of_death: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '6px',
                      border: theme.glass.border,
                      background: theme.card.background,
                      color: theme.text.primary,
                      fontSize: '14px',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', color: theme.text.secondary, fontSize: '14px' }}>
                    City
                  </label>
                  <input
                    type="text"
                    value={editFormData.city}
                    onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '6px',
                      border: theme.glass.border,
                      background: theme.card.background,
                      color: theme.text.primary,
                      fontSize: '14px',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', color: theme.text.secondary, fontSize: '14px' }}>
                    Country
                  </label>
                  <input
                    type="text"
                    value={editFormData.country}
                    onChange={(e) => setEditFormData({ ...editFormData, country: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '6px',
                      border: theme.glass.border,
                      background: theme.card.background,
                      color: theme.text.primary,
                      fontSize: '14px',
                    }}
                  />
                </div>
              </div>
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: theme.glass.border }}>
                <label style={{ display: 'block', marginBottom: '4px', color: theme.text.secondary, fontSize: '14px' }}>
                  Notes
                </label>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                  }}
                />
              </div>
              </>
              )}
              {/* Tabs for additional sections */}
              <div style={{ marginTop: '24px', borderTop: theme.glass.border, paddingTop: '16px' }} className="edit-tabs-container">
                <div 
                  className="edit-tabs-nav"
                  style={{ 
                    display: 'flex', 
                    gap: '8px', 
                    marginBottom: '16px', 
                    borderBottom: theme.glass.border,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  }}
                >
                  <style>{`
                    .edit-tabs-nav::-webkit-scrollbar {
                      display: none;
                    }
                  `}</style>
                  <button
                    type="button"
                    onClick={() => setActiveEditTab('basic')}
                    className="edit-tab-button"
                    style={{
                      padding: 'clamp(10px, 2vw, 12px) clamp(12px, 3vw, 16px)',
                      borderRadius: '6px 6px 0 0',
                      border: 'none',
                      borderBottom: activeEditTab === 'basic' ? `2px solid ${theme.accent.primary}` : '2px solid transparent',
                      background: 'transparent',
                      color: activeEditTab === 'basic' ? theme.accent.primary : theme.text.secondary,
                      fontSize: 'clamp(13px, 2.5vw, 14px)',
                      fontWeight: activeEditTab === 'basic' ? 600 : 400,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      minHeight: '44px',
                      minWidth: 'fit-content',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (activeEditTab !== 'basic') {
                        e.currentTarget.style.background = theme.card.background;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeEditTab !== 'basic') {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    Basic Info
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveEditTab('hobbies')}
                    className="edit-tab-button"
                    style={{
                      padding: 'clamp(10px, 2vw, 12px) clamp(12px, 3vw, 16px)',
                      borderRadius: '6px 6px 0 0',
                      border: 'none',
                      borderBottom: activeEditTab === 'hobbies' ? `2px solid ${theme.accent.primary}` : '2px solid transparent',
                      background: 'transparent',
                      color: activeEditTab === 'hobbies' ? theme.accent.primary : theme.text.secondary,
                      fontSize: 'clamp(13px, 2.5vw, 14px)',
                      fontWeight: activeEditTab === 'hobbies' ? 600 : 400,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      minHeight: '44px',
                      minWidth: 'fit-content',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (activeEditTab !== 'hobbies') {
                        e.currentTarget.style.background = theme.card.background;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeEditTab !== 'hobbies') {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    Hobbies
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveEditTab('contacts')}
                    className="edit-tab-button"
                    style={{
                      padding: 'clamp(10px, 2vw, 12px) clamp(12px, 3vw, 16px)',
                      borderRadius: '6px 6px 0 0',
                      border: 'none',
                      borderBottom: activeEditTab === 'contacts' ? `2px solid ${theme.accent.primary}` : '2px solid transparent',
                      background: 'transparent',
                      color: activeEditTab === 'contacts' ? theme.accent.primary : theme.text.secondary,
                      fontSize: 'clamp(13px, 2.5vw, 14px)',
                      fontWeight: activeEditTab === 'contacts' ? 600 : 400,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      minHeight: '44px',
                      minWidth: 'fit-content',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (activeEditTab !== 'contacts') {
                        e.currentTarget.style.background = theme.card.background;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeEditTab !== 'contacts') {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    Contacts
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveEditTab('social')}
                    className="edit-tab-button"
                    style={{
                      padding: 'clamp(10px, 2vw, 12px) clamp(12px, 3vw, 16px)',
                      borderRadius: '6px 6px 0 0',
                      border: 'none',
                      borderBottom: activeEditTab === 'social' ? `2px solid ${theme.accent.primary}` : '2px solid transparent',
                      background: 'transparent',
                      color: activeEditTab === 'social' ? theme.accent.primary : theme.text.secondary,
                      fontSize: 'clamp(13px, 2.5vw, 14px)',
                      fontWeight: activeEditTab === 'social' ? 600 : 400,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      minHeight: '44px',
                      minWidth: 'fit-content',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (activeEditTab !== 'social') {
                        e.currentTarget.style.background = theme.card.background;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeEditTab !== 'social') {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    Social Media
                  </button>
                </div>

                {/* Tab Content */}
                {activeEditTab === 'basic' && (
                  <div>
                    <p style={{ color: theme.text.secondary, fontSize: '14px' }}>
                      Use the form above to edit basic information. Switch to other tabs to manage hobbies, contacts, and social media.
                    </p>
                  </div>
                )}

                {activeEditTab === 'hobbies' && (
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '12px', color: theme.text.primary }}>Hobbies</h3>
                    {(person?.hobbies_text || person?.hobbies?.length > 0) && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                        {person.hobbies_text && person.hobbies_text.split(',').map((hobby, idx) => {
                          const hobbyText = hobby.trim();
                          if (!hobbyText) return null;
                          if (editingHobby && editingHobby.originalText === hobbyText) {
                            return (
                              <form
                                key={`edit-${idx}`}
                                onSubmit={(e) => handleUpdateHobby(e, hobbyText)}
                                style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                              >
                                <input
                                  type="text"
                                  value={editHobbyText}
                                  onChange={(e) => setEditHobbyText(e.target.value)}
                                  style={{
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    border: theme.glass.border,
                                    background: theme.card.background,
                                    color: theme.text.primary,
                                    fontSize: '14px',
                                    minWidth: '150px',
                                  }}
                                  autoFocus
                                />
                                <button
                                  type="submit"
                                  style={{
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    background: theme.accent.primary,
                                    color: 'white',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  ✓
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingHobby(null);
                                    setEditHobbyText('');
                                  }}
                                  style={{
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    border: theme.glass.border,
                                    background: 'transparent',
                                    color: theme.text.secondary,
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  ✕
                                </button>
                              </form>
                            );
                          }
                          return (
                            <span
                              key={`text-${idx}`}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                background: theme.accent.primary,
                                color: 'white',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              {hobbyText}
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingHobby({ index: idx, originalText: hobbyText });
                                  setEditHobbyText(hobbyText);
                                }}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: 'white',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  padding: 0,
                                  width: '20px',
                                  height: '20px',
                                }}
                                title="Edit hobby"
                              >
                                ✏️
                              </button>
                              <button
                                type="button"
                                onClick={async () => {
                                  const currentHobbies = person.hobbies_text || '';
                                  const hobbyList = currentHobbies.split(',').map(h => h.trim()).filter(h => h && h !== hobbyText);
                                  await peopleAPI.update(id, { hobbies_text: hobbyList.join(', ') });
                                  await loadPerson();
                                }}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: 'white',
                                  cursor: 'pointer',
                                  fontSize: '16px',
                                  padding: 0,
                                  width: '20px',
                                  height: '20px',
                                }}
                                title="Delete hobby"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                        {person.hobbies?.map((hobby) => (
                          <span
                            key={hobby.id}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              background: theme.accent.primary,
                              color: 'white',
                              fontSize: '14px',
                            }}
                          >
                            {hobby.name}
                          </span>
                        ))}
                      </div>
                    )}
                    <form onSubmit={handleAddHobby} style={{ marginTop: '16px' }}>
                      <input
                        type="text"
                        placeholder="Enter hobby (e.g., Reading, Swimming, Cooking)"
                        value={newHobby}
                        onChange={(e) => setNewHobby(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          borderRadius: '6px',
                          border: theme.glass.border,
                          background: theme.card.background,
                          color: theme.text.primary,
                          fontSize: '14px',
                          marginBottom: '12px',
                        }}
                      />
                      <button
                        type="submit"
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          background: theme.accent.primary,
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Add Hobby
                      </button>
                    </form>
                  </div>
                )}

                {activeEditTab === 'contacts' && (
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '12px', color: theme.text.primary }}>Contact Details</h3>
                    {person?.contacts?.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                        {person.contacts.map((contact) => (
                          editingContact?.id === contact.id ? (
                            <form
                              key={`edit-${contact.id}`}
                              onSubmit={handleUpdateContact}
                              style={{
                                padding: '12px',
                                borderRadius: '6px',
                                background: theme.card.background,
                                border: `2px solid ${theme.accent.primary}`,
                              }}
                            >
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '12px' }}>
                                <select
                                  value={editContactData.contact_type}
                                  onChange={(e) => setEditContactData({ ...editContactData, contact_type: e.target.value })}
                                  style={{
                                    padding: '8px',
                                    borderRadius: '6px',
                                    border: theme.glass.border,
                                    background: theme.card.background,
                                    color: theme.text.primary,
                                    fontSize: '14px',
                                  }}
                                >
                                  <option value="phone">Mobile</option>
                                  <option value="whatsapp">WhatsApp</option>
                                  <option value="email">Email</option>
                                </select>
                                <input
                                  type="text"
                                  placeholder="Contact number or email"
                                  value={editContactData.value}
                                  onChange={(e) => setEditContactData({ ...editContactData, value: e.target.value })}
                                  required
                                  style={{
                                    padding: '8px',
                                    borderRadius: '6px',
                                    border: theme.glass.border,
                                    background: theme.card.background,
                                    color: theme.text.primary,
                                    fontSize: '14px',
                                  }}
                                />
                              </div>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: theme.text.secondary, fontSize: '14px' }}>
                                <input
                                  type="checkbox"
                                  checked={editContactData.is_primary}
                                  onChange={(e) => setEditContactData({ ...editContactData, is_primary: e.target.checked })}
                                />
                                Set as primary contact
                              </label>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  type="submit"
                                  style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    background: theme.accent.primary,
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                  }}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingContact(null);
                                    setEditContactData({ contact_type: 'phone', value: '', is_primary: false });
                                  }}
                                  style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: theme.glass.border,
                                    background: 'transparent',
                                    color: theme.text.secondary,
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </form>
                          ) : (
                            <div
                              key={contact.id}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                background: theme.card.background,
                              }}
                            >
                              <div>
                                <span style={{ fontWeight: 600, color: theme.text.primary, marginRight: '8px' }}>
                                  {contact.contact_type === 'whatsapp' ? 'WhatsApp' : contact.contact_type === 'phone' ? 'Mobile' : 'Email'}:
                                </span>
                                <span style={{ color: theme.text.secondary }}>{contact.value}</span>
                                {contact.is_primary && (
                                  <span style={{ marginLeft: '8px', padding: '2px 6px', borderRadius: '4px', background: theme.accent.primary, color: 'white', fontSize: '12px' }}>
                                    Primary
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingContact(contact);
                                    setEditContactData({
                                      contact_type: contact.contact_type,
                                      value: contact.value,
                                      is_primary: contact.is_primary || false,
                                    });
                                  }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: theme.text.secondary,
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    padding: '0 8px',
                                  }}
                                  title="Edit contact"
                                >
                                  ✏️
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteContact(contact.id)}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: theme.text.secondary,
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    padding: '0 8px',
                                  }}
                                  title="Delete contact"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                    <form onSubmit={handleAddContact} style={{ marginTop: '16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '12px' }}>
                        <select
                          value={newContact.contact_type}
                          onChange={(e) => setNewContact({ ...newContact, contact_type: e.target.value })}
                          style={{
                            padding: '8px',
                            borderRadius: '6px',
                            border: theme.glass.border,
                            background: theme.card.background,
                            color: theme.text.primary,
                            fontSize: '14px',
                          }}
                        >
                          <option value="phone">Mobile</option>
                          <option value="whatsapp">WhatsApp</option>
                          <option value="email">Email</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Contact number or email"
                          value={newContact.value}
                          onChange={(e) => setNewContact({ ...newContact, value: e.target.value })}
                          required
                          style={{
                            padding: '8px',
                            borderRadius: '6px',
                            border: theme.glass.border,
                            background: theme.card.background,
                            color: theme.text.primary,
                            fontSize: '14px',
                          }}
                        />
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: theme.text.secondary, fontSize: '14px' }}>
                        <input
                          type="checkbox"
                          checked={newContact.is_primary}
                          onChange={(e) => setNewContact({ ...newContact, is_primary: e.target.checked })}
                        />
                        Set as primary contact
                      </label>
                      <button
                        type="submit"
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          background: theme.accent.primary,
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Add Contact
                      </button>
                    </form>
                  </div>
                )}

                {activeEditTab === 'social' && (
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '12px', color: theme.text.primary }}>Social Media Links</h3>
                    {person?.social_links?.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                        {person.social_links.map((link) => (
                          editingSocialLink?.id === link.id ? (
                            <form
                              key={`edit-${link.id}`}
                              onSubmit={handleUpdateSocialLink}
                              style={{
                                padding: '12px',
                                borderRadius: '6px',
                                background: theme.card.background,
                                border: `2px solid ${theme.accent.primary}`,
                              }}
                            >
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '12px' }}>
                                <select
                                  value={editSocialLinkData.platform}
                                  onChange={(e) => setEditSocialLinkData({ ...editSocialLinkData, platform: e.target.value })}
                                  style={{
                                    padding: '8px',
                                    borderRadius: '6px',
                                    border: theme.glass.border,
                                    background: theme.card.background,
                                    color: theme.text.primary,
                                    fontSize: '14px',
                                  }}
                                >
                                  <option value="facebook">Facebook</option>
                                  <option value="twitter">Twitter</option>
                                  <option value="instagram">Instagram</option>
                                  <option value="linkedin">LinkedIn</option>
                                  <option value="youtube">YouTube</option>
                                  <option value="tiktok">TikTok</option>
                                  <option value="other">Other</option>
                                </select>
                                <input
                                  type="url"
                                  placeholder="https://..."
                                  value={editSocialLinkData.url}
                                  onChange={(e) => setEditSocialLinkData({ ...editSocialLinkData, url: e.target.value })}
                                  required
                                  style={{
                                    padding: '8px',
                                    borderRadius: '6px',
                                    border: theme.glass.border,
                                    background: theme.card.background,
                                    color: theme.text.primary,
                                    fontSize: '14px',
                                  }}
                                />
                              </div>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  type="submit"
                                  style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    background: theme.accent.primary,
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                  }}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingSocialLink(null);
                                    setEditSocialLinkData({ platform: 'facebook', url: '' });
                                  }}
                                  style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: theme.glass.border,
                                    background: 'transparent',
                                    color: theme.text.secondary,
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </form>
                          ) : (
                            <div
                              key={link.id}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                background: theme.card.background,
                              }}
                            >
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: theme.accent.primary,
                                  textDecoration: 'none',
                                  fontSize: '14px',
                                }}
                              >
                                {link.platform.charAt(0).toUpperCase() + link.platform.slice(1)}: {link.url}
                              </a>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingSocialLink(link);
                                    setEditSocialLinkData({
                                      platform: link.platform,
                                      url: link.url,
                                    });
                                  }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: theme.text.secondary,
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    padding: '0 8px',
                                  }}
                                  title="Edit social link"
                                >
                                  ✏️
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteSocialLink(link.id)}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: theme.text.secondary,
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    padding: '0 8px',
                                  }}
                                  title="Delete social link"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                    <form onSubmit={handleAddSocialLink} style={{ marginTop: '16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '12px' }} className="responsive-form-grid">
                        <select
                          value={newSocialLink.platform}
                          onChange={(e) => setNewSocialLink({ ...newSocialLink, platform: e.target.value })}
                          style={{
                            padding: '8px',
                            borderRadius: '6px',
                            border: theme.glass.border,
                            background: theme.card.background,
                            color: theme.text.primary,
                            fontSize: '14px',
                          }}
                        >
                          <option value="facebook">Facebook</option>
                          <option value="twitter">Twitter</option>
                          <option value="instagram">Instagram</option>
                          <option value="linkedin">LinkedIn</option>
                          <option value="youtube">YouTube</option>
                          <option value="tiktok">TikTok</option>
                          <option value="other">Other</option>
                        </select>
                        <input
                          type="url"
                          placeholder="https://..."
                          value={newSocialLink.url}
                          onChange={(e) => setNewSocialLink({ ...newSocialLink, url: e.target.value })}
                          required
                          style={{
                            padding: '8px',
                            borderRadius: '6px',
                            border: theme.glass.border,
                            background: theme.card.background,
                            color: theme.text.primary,
                            fontSize: '14px',
                          }}
                        />
                      </div>
                      <button
                        type="submit"
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          background: theme.accent.primary,
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Add Link
                      </button>
                    </form>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: 'none',
                    background: theme.accent.primary,
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setActiveEditTab('basic');
                    if (person) {
                      setEditFormData({
                        first_name: person.first_name || '',
                        middle_name: person.middle_name || '',
                        last_name: person.last_name || '',
                        preferred_name: person.preferred_name || '',
                        sex: person.sex || 'unknown',
                        date_of_birth: formatDateForInput(person.date_of_birth),
                        date_of_death: formatDateForInput(person.date_of_death),
                        city: person.city || '',
                        country: person.country || '',
                        notes: person.notes || '',
                      });
                    }
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: 'transparent',
                    color: theme.text.primary,
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </GlassCard>
        </div>
      )}

      {/* Immediate Relationships - Full width for better visibility */}
      <GlassCard style={{ marginBottom: '24px', padding: 'clamp(16px, 4vw, 24px)' }}>
        <h2 style={{ fontSize: 'clamp(20px, 4vw, 24px)', marginBottom: '16px', color: theme.text.primary }}>Relationships</h2>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: '16px', 
          alignItems: 'stretch' 
        }}
        className="relationships-container"
        >
          {/* Relationships Display - Left Side */}
          <div style={{ flex: 1, width: '100%' }}>
            {(person.parents?.length > 0 || person.children?.length > 0 || person.spouses?.length > 0 || person.siblings?.length > 0 || person.in_laws?.length > 0) ? (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', 
                gap: '12px' 
              }}
              className="relationship-grid"
              >
            {person.parents?.length > 0 && (
              <div>
                <h3 style={{ fontSize: '16px', marginBottom: '8px', color: theme.text.secondary }}>Parents</h3>
                {person.parents.map((parent) => {
                  // Determine if parent is Father or Mother based on sex
                  const parentRole = parent.sex === 'male' ? 'Father' : parent.sex === 'female' ? 'Mother' : 'Parent';
                  const parentColor = parent.sex === 'male' ? 'rgba(37, 99, 235, 0.2)' : parent.sex === 'female' ? 'rgba(234, 88, 12, 0.2)' : 'rgba(107, 114, 128, 0.2)';
                  return (
                    <div
                      key={parent.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        borderRadius: '6px',
                        background: parentColor,
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: `1px solid ${parent.sex === 'male' ? 'rgba(37, 99, 235, 0.3)' : parent.sex === 'female' ? 'rgba(234, 88, 12, 0.3)' : 'rgba(107, 114, 128, 0.3)'}`,
                        marginBottom: '4px',
                      }}
                    >
                      <Link
                        to={`/people/${parent.id}`}
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          textDecoration: 'none',
                          color: theme.text.primary,
                        }}
                      >
                        {parent.picture_url && (
                          <img
                            src={parent.picture_url}
                            alt={parent.preferred_name || parent.first_name}
                            style={{ width: '40px', height: '40px', borderRadius: '50%', marginRight: '8px' }}
                          />
                        )}
                        <span style={{ fontSize: '14px', marginRight: '4px', color: theme.text.primary }}>
                          {parent.sex === 'male' ? '♂' : parent.sex === 'female' ? '♀' : '⚥'}
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span>{parent.preferred_name || parent.first_name} {parent.last_name || ''}</span>
                          <span style={{ fontSize: '12px', color: theme.text.secondary }}>{parentRole}</span>
                        </div>
                      </Link>
                      {canEdit && (
                        <button
                          onClick={() => setEditingRelationship({ type: 'parent', personId: parent.id, ...parent })}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: theme.text.secondary,
                            cursor: 'pointer',
                            fontSize: '20px',
                            padding: '8px',
                            minWidth: '44px',
                            minHeight: '44px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title="Edit relationship"
                        >
                          ✏️
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {person.in_laws?.length > 0 && (
              <div>
                <h3 style={{ fontSize: '16px', marginBottom: '8px', color: theme.text.secondary }}>In-Laws</h3>
                {person.in_laws.map((inLaw) => {
                  const inLawRole = inLaw.sex === 'male' ? 'Father-in-Law' : inLaw.sex === 'female' ? 'Mother-in-Law' : 'In-Law';
                  const inLawColor = inLaw.sex === 'male' ? 'rgba(37, 99, 235, 0.2)' : inLaw.sex === 'female' ? 'rgba(234, 88, 12, 0.2)' : 'rgba(107, 114, 128, 0.2)';
                  return (
                    <div
                      key={inLaw.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        borderRadius: '6px',
                        background: inLawColor,
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: `1px solid ${inLaw.sex === 'male' ? 'rgba(37, 99, 235, 0.3)' : inLaw.sex === 'female' ? 'rgba(234, 88, 12, 0.3)' : 'rgba(107, 114, 128, 0.3)'}`,
                        marginBottom: '4px',
                      }}
                    >
                      <Link
                        to={`/people/${inLaw.id}`}
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          textDecoration: 'none',
                          color: theme.text.primary,
                        }}
                      >
                        {inLaw.picture_url && (
                          <img
                            src={inLaw.picture_url}
                            alt={inLaw.preferred_name || inLaw.first_name}
                            style={{ width: '40px', height: '40px', borderRadius: '50%', marginRight: '8px' }}
                          />
                        )}
                        <span style={{ fontSize: '14px', marginRight: '4px', color: theme.text.primary }}>
                          {inLaw.sex === 'male' ? '♂' : inLaw.sex === 'female' ? '♀' : '⚥'}
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span>{inLaw.preferred_name || inLaw.first_name} {inLaw.last_name || ''}</span>
                          <span style={{ fontSize: '12px', color: theme.text.secondary }}>{inLawRole}</span>
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
            {person.siblings?.length > 0 && (
              <div>
                <h3 style={{ fontSize: '16px', marginBottom: '8px', color: theme.text.secondary }}>Siblings</h3>
                {person.siblings.map((sibling) => {
                  const siblingRole = sibling.sex === 'male' ? 'Brother' : sibling.sex === 'female' ? 'Sister' : 'Sibling';
                  const siblingColor = sibling.sex === 'male' ? 'rgba(37, 99, 235, 0.2)' : sibling.sex === 'female' ? 'rgba(234, 88, 12, 0.2)' : 'rgba(107, 114, 128, 0.2)';
                  return (
                    <div
                      key={sibling.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        borderRadius: '6px',
                        background: siblingColor,
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: `1px solid ${sibling.sex === 'male' ? 'rgba(37, 99, 235, 0.3)' : sibling.sex === 'female' ? 'rgba(234, 88, 12, 0.3)' : 'rgba(107, 114, 128, 0.3)'}`,
                        marginBottom: '4px',
                      }}
                    >
                      <Link
                        to={`/people/${sibling.id}`}
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          textDecoration: 'none',
                          color: theme.text.primary,
                        }}
                      >
                        {sibling.picture_url && (
                          <img
                            src={sibling.picture_url}
                            alt={sibling.preferred_name || sibling.first_name}
                            style={{ width: '40px', height: '40px', borderRadius: '50%', marginRight: '8px' }}
                          />
                        )}
                        <span style={{ fontSize: '14px', marginRight: '4px', color: theme.text.primary }}>
                          {sibling.sex === 'male' ? '♂' : sibling.sex === 'female' ? '♀' : '⚥'}
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span>{sibling.preferred_name || sibling.first_name} {sibling.last_name || ''}</span>
                          <span style={{ fontSize: '12px', color: theme.text.secondary }}>{siblingRole}</span>
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
            {person.spouses?.length > 0 && (
              <div>
                <h3 style={{ fontSize: '16px', marginBottom: '8px', color: theme.text.secondary }}>Spouse/Partner</h3>
                {person.spouses.map((spouse) => {
                  const statusLabels = {
                    'married': '💒 Married',
                    'divorced': '💔 Divorced',
                    'widowed': '🕯️ Widowed'
                  };
                  const statusLabel = statusLabels[spouse.status] || spouse.status || 'Married';
                  const spouseColor = spouse.sex === 'male' ? 'rgba(37, 99, 235, 0.2)' : spouse.sex === 'female' ? 'rgba(234, 88, 12, 0.2)' : 'rgba(107, 114, 128, 0.2)';
                  return (
                    <div
                      key={spouse.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        borderRadius: '6px',
                        background: spouseColor,
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: `1px solid ${spouse.sex === 'male' ? 'rgba(37, 99, 235, 0.3)' : spouse.sex === 'female' ? 'rgba(234, 88, 12, 0.3)' : 'rgba(107, 114, 128, 0.3)'}`,
                        marginBottom: '4px',
                      }}
                    >
                      <Link
                        to={`/people/${spouse.id}`}
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          textDecoration: 'none',
                          color: theme.text.primary,
                        }}
                      >
                        {spouse.picture_url && (
                          <img
                            src={spouse.picture_url}
                            alt={spouse.preferred_name || spouse.first_name}
                            style={{ width: '40px', height: '40px', borderRadius: '50%', marginRight: '8px' }}
                          />
                        )}
                        <span style={{ fontSize: '14px', marginRight: '4px', color: theme.text.primary }}>
                          {spouse.sex === 'male' ? '♂' : spouse.sex === 'female' ? '♀' : '⚥'}
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span>{spouse.preferred_name || spouse.first_name} {spouse.last_name || ''}</span>
                          <span style={{ fontSize: '12px', color: theme.text.secondary }}>{statusLabel}</span>
                        </div>
                      </Link>
                      {canEdit && (
                        <button
                          onClick={() => setEditingRelationship({ type: 'spouse', personId: spouse.id, ...spouse })}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: theme.text.secondary,
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '4px 8px',
                          }}
                          title="Edit relationship"
                        >
                          ✏️
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {person.children?.length > 0 && (
              <div>
                <h3 style={{ fontSize: '16px', marginBottom: '8px', color: theme.text.secondary }}>Children</h3>
                {person.children.map((child) => {
                  const childRole = child.sex === 'male' ? 'Son' : child.sex === 'female' ? 'Daughter' : 'Child';
                  const childColor = child.sex === 'male' ? 'rgba(37, 99, 235, 0.2)' : child.sex === 'female' ? 'rgba(234, 88, 12, 0.2)' : 'rgba(107, 114, 128, 0.2)';
                  return (
                    <div
                      key={child.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        borderRadius: '6px',
                        background: childColor,
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: `1px solid ${child.sex === 'male' ? 'rgba(37, 99, 235, 0.3)' : child.sex === 'female' ? 'rgba(234, 88, 12, 0.3)' : 'rgba(107, 114, 128, 0.3)'}`,
                        marginBottom: '4px',
                      }}
                    >
                      <Link
                        to={`/people/${child.id}`}
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          textDecoration: 'none',
                          color: theme.text.primary,
                        }}
                      >
                        {child.picture_url && (
                          <img
                            src={child.picture_url}
                            alt={child.preferred_name || child.first_name}
                            style={{ width: '40px', height: '40px', borderRadius: '50%', marginRight: '8px' }}
                          />
                        )}
                        <span style={{ fontSize: '14px', marginRight: '4px', color: theme.text.primary }}>
                          {child.sex === 'male' ? '♂' : child.sex === 'female' ? '♀' : '⚥'}
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span>{child.preferred_name || child.first_name} {child.last_name || ''}</span>
                          <span style={{ fontSize: '12px', color: theme.text.secondary }}>{childRole}</span>
                        </div>
                      </Link>
                      {canEdit && (
                        <button
                          onClick={() => setEditingRelationship({ type: 'child', personId: child.id, ...child })}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: theme.text.secondary,
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '4px 8px',
                          }}
                          title="Edit relationship"
                        >
                          ✏️
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
              </div>
            ) : (
              <p style={{ color: theme.text.secondary, fontStyle: 'italic' }}>No relationships added yet. Use the buttons to the right to add parents, children, or partners.</p>
            )}
          </div>
          
          {/* Relationship Buttons - Right Side, Stacked Vertically */}
          {familyId && canEdit && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '180px', flexShrink: 0 }}>
              <button
                onClick={() => {
                  setShowAddParentForm(!showAddParentForm);
                  setShowAddChildForm(false);
                  setShowAddPartnerForm(false);
                  setShowAddSiblingForm(false);
                  setLinkData({ related_person_id: '', relationship_type: '', relationship_role: '', spouse_role: '', sibling_role: '', child_role: '', new_person: { first_name: '', last_name: '', preferred_name: '', date_of_birth: '' }, link_existing: true });
                  setError(null);
                }}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: theme.glass.border,
                  background: showAddParentForm ? theme.card.background : theme.accent.primary,
                  color: showAddParentForm ? theme.text.primary : 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'all 0.3s ease',
                }}
              >
                + Add Parent
              </button>
              <button
                onClick={() => {
                  setShowAddChildForm(!showAddChildForm);
                  setShowAddParentForm(false);
                  setShowAddPartnerForm(false);
                  setShowAddSiblingForm(false);
                  setLinkData({ related_person_id: '', relationship_type: '', relationship_role: '', spouse_role: '', sibling_role: '', child_role: '', new_person: { first_name: '', last_name: '', preferred_name: '', date_of_birth: '' }, link_existing: true });
                  setError(null);
                }}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: theme.glass.border,
                  background: showAddChildForm ? theme.card.background : theme.accent.primary,
                  color: showAddChildForm ? theme.text.primary : 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'all 0.3s ease',
                }}
              >
                + Add Children
              </button>
              <button
                onClick={() => {
                  setShowAddPartnerForm(!showAddPartnerForm);
                  setShowAddParentForm(false);
                  setShowAddChildForm(false);
                  setShowAddSiblingForm(false);
                  setLinkData({ related_person_id: '', relationship_type: '', relationship_role: '', spouse_role: '', sibling_role: '', child_role: '', new_person: { first_name: '', last_name: '', preferred_name: '', date_of_birth: '' }, link_existing: true });
                  setError(null);
                }}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: theme.glass.border,
                  background: showAddPartnerForm ? theme.card.background : theme.accent.primary,
                  color: showAddPartnerForm ? theme.text.primary : 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'all 0.3s ease',
                }}
              >
                + Add Spouse
              </button>
              <button
                onClick={() => {
                  setShowAddSiblingForm(!showAddSiblingForm);
                  setShowAddParentForm(false);
                  setShowAddChildForm(false);
                  setShowAddPartnerForm(false);
                  setLinkData({ related_person_id: '', relationship_type: '', relationship_role: '', spouse_role: '', sibling_role: '', child_role: '', new_person: { first_name: '', last_name: '', preferred_name: '', date_of_birth: '' }, link_existing: true });
                  setError(null);
                }}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: theme.glass.border,
                  background: showAddSiblingForm ? theme.card.background : theme.accent.primary,
                  color: showAddSiblingForm ? theme.text.primary : 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'all 0.3s ease',
                }}
              >
                + Add Sibling
              </button>
            </div>
          )}
        </div>
      </GlassCard>

      {/* Edit Relationship Modal */}
      {editingRelationship && (
        <GlassCard style={{ marginBottom: '24px', position: 'relative' }}>
          <h3 style={{ fontSize: '20px', marginBottom: '16px', color: theme.text.primary }}>
            Edit Relationship with {editingRelationship.preferred_name || editingRelationship.first_name}
          </h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const updates = {};
              if (editingRelationship.type === 'parent' || editingRelationship.type === 'child') {
                updates.kind = formData.get('kind');
              } else if (editingRelationship.type === 'spouse') {
                updates.partnership_type = formData.get('partnership_type');
              }
              handleUpdateRelationship(editingRelationship.type, editingRelationship, updates);
            }}
          >
            {(editingRelationship.type === 'parent' || editingRelationship.type === 'child') && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: '14px' }}>
                  Relationship Type
                </label>
                <select
                  name="kind"
                  defaultValue={editingRelationship.relationship_kind || 'biological'}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '14px',
                  }}
                >
                  <option value="biological">Biological</option>
                  <option value="adoptive">Adoptive</option>
                  <option value="step">Step</option>
                  <option value="guardian">Guardian</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
            )}
            {editingRelationship.type === 'spouse' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: '14px' }}>
                  Partnership Type
                </label>
                <select
                  name="partnership_type"
                  defaultValue={editingRelationship.partnership_type || 'spouse'}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '14px',
                  }}
                >
                  <option value="spouse">Spouse</option>
                  <option value="partner">Partner</option>
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  background: theme.accent.primary,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Update Relationship
              </button>
              <button
                type="button"
                onClick={() => handleDeleteRelationship(editingRelationship.type, editingRelationship)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: 'rgba(255, 0, 0, 0.1)',
                  color: '#ff4444',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Delete Relationship
              </button>
              <button
                type="button"
                onClick={() => setEditingRelationship(null)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: 'transparent',
                  color: theme.text.primary,
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </GlassCard>
      )}

      {/* Hobbies Section */}
      {familyId && (person.hobbies_text || person.hobbies?.length > 0 || (isClaimedByCurrentUser && canEdit)) && (
        <GlassCard style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '24px', color: theme.text.primary }}>Hobbies</h2>
            {isClaimedByCurrentUser && canEdit && (
              <button
                onClick={() => setShowHobbiesForm(!showHobbiesForm)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: theme.accent.primary,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {showHobbiesForm ? 'Cancel' : '+ Add Hobby'}
              </button>
            )}
          </div>
          {(person.hobbies_text || person.hobbies?.length > 0) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
              {person.hobbies_text && person.hobbies_text.split(',').map((hobby, idx) => {
                const hobbyText = hobby.trim();
                if (!hobbyText) return null;
                if (editingHobby && editingHobby.originalText === hobbyText) {
                  return (
                    <form
                      key={`edit-${idx}`}
                      onSubmit={(e) => handleUpdateHobby(e, hobbyText)}
                      style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                    >
                      <input
                        type="text"
                        value={editHobbyText}
                        onChange={(e) => setEditHobbyText(e.target.value)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: theme.glass.border,
                          background: theme.card.background,
                          color: theme.text.primary,
                          fontSize: '14px',
                          minWidth: '150px',
                        }}
                        autoFocus
                      />
                      <button
                        type="submit"
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          background: theme.accent.primary,
                          color: 'white',
                          fontSize: '14px',
                          cursor: 'pointer',
                        }}
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingHobby(null);
                          setEditHobbyText('');
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: theme.glass.border,
                          background: 'transparent',
                          color: theme.text.secondary,
                          fontSize: '14px',
                          cursor: 'pointer',
                        }}
                      >
                        ✕
                      </button>
                    </form>
                  );
                }
                return (
                  <span
                    key={`text-${idx}`}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      background: theme.accent.primary,
                      color: 'white',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {hobbyText}
                    {isClaimedByCurrentUser && canEdit && (
                      <>
                        <button
                          onClick={() => {
                            setEditingHobby({ index: idx, originalText: hobbyText });
                            setEditHobbyText(hobbyText);
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '14px',
                            padding: 0,
                            width: '20px',
                            height: '20px',
                          }}
                          title="Edit hobby"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleRemoveHobby(hobbyText)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: 0,
                            width: '20px',
                            height: '20px',
                          }}
                          title="Delete hobby"
                        >
                          ×
                        </button>
                      </>
                    )}
                  </span>
                );
              })}
              {person.hobbies?.map((hobby) => (
                <span
                  key={hobby.id}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    background: theme.accent.primary,
                    color: 'white',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {hobby.name}
                </span>
              ))}
            </div>
          )}
          {showHobbiesForm && (
            <form onSubmit={handleAddHobby} style={{ paddingTop: '16px', borderTop: theme.glass.border }}>
              <input
                type="text"
                placeholder="Enter hobby (e.g., Reading, Swimming, Cooking)"
                value={newHobby}
                onChange={(e) => setNewHobby(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: theme.card.background,
                  color: theme.text.primary,
                  fontSize: '14px',
                  marginBottom: '12px',
                }}
              />
              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  background: theme.accent.primary,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Add Hobby
              </button>
            </form>
          )}
        </GlassCard>
      )}

      {/* Contact Details Section */}
      {(person.contacts?.length > 0 || (isClaimedByCurrentUser && canEdit)) && (
        <GlassCard style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '24px', color: theme.text.primary }}>Contact Details</h2>
            {isClaimedByCurrentUser && canEdit && (
              <button
                onClick={() => setShowContactForm(!showContactForm)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: theme.accent.primary,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {showContactForm ? 'Cancel' : '+ Add Contact'}
              </button>
            )}
          </div>
          {person.contacts?.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {person.contacts.map((contact) => (
                editingContact?.id === contact.id ? (
                  <form
                    key={`edit-${contact.id}`}
                    onSubmit={handleUpdateContact}
                    style={{
                      padding: '12px',
                      borderRadius: '6px',
                      background: theme.card.background,
                      border: `2px solid ${theme.accent.primary}`,
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '12px' }}>
                      <select
                        value={editContactData.contact_type}
                        onChange={(e) => setEditContactData({ ...editContactData, contact_type: e.target.value })}
                        style={{
                          padding: '8px',
                          borderRadius: '6px',
                          border: theme.glass.border,
                          background: theme.card.background,
                          color: theme.text.primary,
                          fontSize: '14px',
                        }}
                      >
                        <option value="phone">Mobile</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="email">Email</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Contact number or email"
                        value={editContactData.value}
                        onChange={(e) => setEditContactData({ ...editContactData, value: e.target.value })}
                        required
                        style={{
                          padding: '8px',
                          borderRadius: '6px',
                          border: theme.glass.border,
                          background: theme.card.background,
                          color: theme.text.primary,
                          fontSize: '14px',
                        }}
                      />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: theme.text.secondary, fontSize: '14px' }}>
                      <input
                        type="checkbox"
                        checked={editContactData.is_primary}
                        onChange={(e) => setEditContactData({ ...editContactData, is_primary: e.target.checked })}
                      />
                      Set as primary contact
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="submit"
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          background: theme.accent.primary,
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingContact(null);
                          setEditContactData({ contact_type: 'phone', value: '', is_primary: false });
                        }}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: theme.glass.border,
                          background: 'transparent',
                          color: theme.text.secondary,
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div
                    key={contact.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      background: theme.card.background,
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: 600, color: theme.text.primary, marginRight: '8px' }}>
                        {contact.contact_type === 'whatsapp' ? 'WhatsApp' : contact.contact_type === 'phone' ? 'Mobile' : 'Email'}:
                      </span>
                      <span style={{ color: theme.text.secondary }}>{contact.value}</span>
                      {contact.is_primary && (
                        <span style={{ marginLeft: '8px', padding: '2px 6px', borderRadius: '4px', background: theme.accent.primary, color: 'white', fontSize: '12px' }}>
                          Primary
                        </span>
                      )}
                    </div>
                    {isClaimedByCurrentUser && canEdit && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => {
                            setEditingContact(contact);
                            setEditContactData({
                              contact_type: contact.contact_type,
                              value: contact.value,
                              is_primary: contact.is_primary || false,
                            });
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: theme.text.secondary,
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '0 8px',
                          }}
                          title="Edit contact"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteContact(contact.id)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: theme.text.secondary,
                            cursor: 'pointer',
                            fontSize: '18px',
                            padding: '0 8px',
                          }}
                          title="Delete contact"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                )
              ))}
            </div>
          )}
          {showContactForm && (
            <form onSubmit={handleAddContact} style={{ paddingTop: '16px', borderTop: theme.glass.border }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '12px' }}>
                <select
                  value={newContact.contact_type}
                  onChange={(e) => setNewContact({ ...newContact, contact_type: e.target.value })}
                  style={{
                    padding: '8px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '14px',
                  }}
                >
                  <option value="phone">Mobile</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="email">Email</option>
                </select>
                <input
                  type="text"
                  placeholder="Contact number or email"
                  value={newContact.value}
                  onChange={(e) => setNewContact({ ...newContact, value: e.target.value })}
                  required
                  style={{
                    padding: '8px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '14px',
                  }}
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: theme.text.secondary, fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={newContact.is_primary}
                  onChange={(e) => setNewContact({ ...newContact, is_primary: e.target.checked })}
                />
                Set as primary contact
              </label>
              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  background: theme.accent.primary,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Add Contact
              </button>
            </form>
          )}
        </GlassCard>
      )}

      {/* Social Links Section */}
      {(person.social_links?.length > 0 || (isClaimedByCurrentUser && canEdit)) && (
        <GlassCard style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '24px', color: theme.text.primary }}>Social Media Links</h2>
            {isClaimedByCurrentUser && canEdit && (
              <button
                onClick={() => setShowSocialLinkForm(!showSocialLinkForm)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: theme.accent.primary,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {showSocialLinkForm ? 'Cancel' : '+ Add Link'}
              </button>
            )}
          </div>
          {person.social_links?.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {person.social_links.map((link) => (
                editingSocialLink?.id === link.id ? (
                  <form
                    key={`edit-${link.id}`}
                    onSubmit={handleUpdateSocialLink}
                    style={{
                      padding: '12px',
                      borderRadius: '6px',
                      background: theme.card.background,
                      border: `2px solid ${theme.accent.primary}`,
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '12px' }}>
                      <select
                        value={editSocialLinkData.platform}
                        onChange={(e) => setEditSocialLinkData({ ...editSocialLinkData, platform: e.target.value })}
                        style={{
                          padding: '8px',
                          borderRadius: '6px',
                          border: theme.glass.border,
                          background: theme.card.background,
                          color: theme.text.primary,
                          fontSize: '14px',
                        }}
                      >
                        <option value="facebook">Facebook</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="instagram">Instagram</option>
                        <option value="x">X (Twitter)</option>
                        <option value="tiktok">TikTok</option>
                        <option value="website">Website</option>
                        <option value="other">Other</option>
                      </select>
                      <input
                        type="url"
                        placeholder="URL"
                        value={editSocialLinkData.url}
                        onChange={(e) => setEditSocialLinkData({ ...editSocialLinkData, url: e.target.value })}
                        required
                        style={{
                          padding: '8px',
                          borderRadius: '6px',
                          border: theme.glass.border,
                          background: theme.card.background,
                          color: theme.text.primary,
                          fontSize: '14px',
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="submit"
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          background: theme.accent.primary,
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingSocialLink(null);
                          setEditSocialLinkData({ platform: 'facebook', url: '' });
                        }}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: theme.glass.border,
                          background: 'transparent',
                          color: theme.text.secondary,
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div
                    key={link.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      background: theme.card.background,
                    }}
                  >
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: theme.accent.primary,
                        textDecoration: 'none',
                        fontSize: '14px',
                      }}
                    >
                      {link.platform.charAt(0).toUpperCase() + link.platform.slice(1)}: {link.url}
                    </a>
                    {isClaimedByCurrentUser && canEdit && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => {
                            setEditingSocialLink(link);
                            setEditSocialLinkData({
                              platform: link.platform,
                              url: link.url,
                            });
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: theme.text.secondary,
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '0 8px',
                          }}
                          title="Edit social link"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteSocialLink(link.id)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: theme.text.secondary,
                            cursor: 'pointer',
                            fontSize: '18px',
                            padding: '0 8px',
                          }}
                          title="Delete social link"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                )
              ))}
            </div>
          )}
          {showSocialLinkForm && (
            <form onSubmit={handleAddSocialLink} style={{ paddingTop: '16px', borderTop: theme.glass.border }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '12px' }}>
                <select
                  value={newSocialLink.platform}
                  onChange={(e) => setNewSocialLink({ ...newSocialLink, platform: e.target.value })}
                  style={{
                    padding: '8px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '14px',
                  }}
                >
                  <option value="facebook">Facebook</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="instagram">Instagram</option>
                  <option value="x">X (Twitter)</option>
                  <option value="tiktok">TikTok</option>
                  <option value="website">Website</option>
                  <option value="other">Other</option>
                </select>
                <input
                  type="url"
                  placeholder="URL"
                  value={newSocialLink.url}
                  onChange={(e) => setNewSocialLink({ ...newSocialLink, url: e.target.value })}
                  required
                  style={{
                    padding: '8px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '14px',
                  }}
                />
              </div>
              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  background: theme.accent.primary,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Add Link
              </button>
            </form>
          )}
        </GlassCard>
      )}

      {error && !showAddParentForm && !showAddChildForm && !showAddPartnerForm && !showAddSiblingForm && (
        <GlassCard style={{ marginTop: '24px', background: 'rgba(255, 0, 0, 0.1)' }}>
          <p style={{ color: theme.text.primary }}>Error: {error}</p>
        </GlassCard>
      )}

      {/* Relationship Forms Section - Below Relationships */}
      {familyId && (
        <GlassCard style={{ marginTop: '24px', padding: 'clamp(16px, 4vw, 24px)' }}>
          {/* Add Parent Form */}
          {showAddParentForm && (
            <form 
              ref={addParentFormRef}
              onSubmit={handleAddParent} 
              style={{ marginTop: '16px', paddingTop: '16px', borderTop: theme.glass.border, scrollMarginTop: '80px' }} 
              className="relationship-form"
            >
              {error && (
                <div style={{ marginBottom: '12px', padding: 'clamp(8px, 2vw, 12px)', borderRadius: '6px', background: 'rgba(255, 0, 0, 0.1)' }}>
                  <p style={{ color: theme.text.primary, fontSize: 'clamp(13px, 2.5vw, 14px)', margin: 0, wordBreak: 'break-word' }}>Error: {error}</p>
                </div>
              )}
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: theme.text.secondary, fontSize: 'clamp(14px, 3vw, 16px)', minHeight: '44px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={linkData.link_existing}
                    onChange={() => setLinkData({ ...linkData, link_existing: true })}
                    style={{ margin: 0, width: '20px', height: '20px', minWidth: '20px', minHeight: '20px', cursor: 'pointer' }}
                  />
                  Link to existing member
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: theme.text.secondary, fontSize: 'clamp(14px, 3vw, 16px)', minHeight: '44px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={!linkData.link_existing}
                    onChange={() => setLinkData({ ...linkData, link_existing: false })}
                    style={{ margin: 0, width: '20px', height: '20px', minWidth: '20px', minHeight: '20px', cursor: 'pointer' }}
                  />
                  Create new parent
                </label>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: 'clamp(13px, 2.5vw, 14px)' }}>
                  Parent Role
                </label>
                <select
                  value={linkData.relationship_role}
                  onChange={(e) => setLinkData({ ...linkData, relationship_role: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '16px',
                    marginBottom: '12px',
                    minHeight: '44px',
                  }}
                >
                  <option value="">Select role</option>
                  <option value="father">Father</option>
                  <option value="mother">Mother</option>
                </select>
              </div>

              {linkData.link_existing ? (
                <select
                  value={linkData.related_person_id}
                  onChange={(e) => setLinkData({ ...linkData, related_person_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '16px',
                    marginBottom: '12px',
                    minHeight: '44px',
                  }}
                >
                  <option value="">Select a parent</option>
                  {familyMembers
                    .filter((member) => member.id !== id)
                    .map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.preferred_name || member.first_name} {member.last_name || ''}
                      </option>
                    ))}
                </select>
              ) : (
                <div className="relationship-form-grid">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '12px', marginBottom: '12px' }}>
                    <input
                      type="text"
                      placeholder="First Name *"
                      value={linkData.new_person.first_name}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, first_name: e.target.value } })}
                      required
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Surname (Maiden)"
                      value={linkData.new_person.last_name}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, last_name: e.target.value } })}
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '12px', marginBottom: '12px' }}>
                    <input
                      type="text"
                      placeholder="Preferred Name"
                      value={linkData.new_person.preferred_name}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, preferred_name: e.target.value } })}
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                    <input
                      type="date"
                      placeholder="Date of Birth"
                      value={linkData.new_person.date_of_birth}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, date_of_birth: e.target.value } })}
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  type="submit"
                  style={{
                    padding: '12px 20px',
                    borderRadius: '6px',
                    border: 'none',
                    background: theme.accent.primary,
                    color: 'white',
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: '44px',
                    flex: 1,
                    minWidth: '120px',
                  }}
                >
                  Add Parent
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddParentForm(false);
                    setLinkData({ related_person_id: '', relationship_role: '', new_person: { first_name: '', last_name: '', preferred_name: '', date_of_birth: '' }, link_existing: true });
                  }}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: 'transparent',
                    color: theme.text.primary,
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: '44px',
                    flex: 1,
                    minWidth: '120px',
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Add Child Form */}
          {showAddChildForm && (
            <form 
              ref={addChildFormRef}
              onSubmit={handleAddChild} 
              style={{ marginTop: '16px', paddingTop: '16px', borderTop: theme.glass.border, scrollMarginTop: '80px' }} 
              className="relationship-form"
            >
              {error && (
                <div style={{ marginBottom: '12px', padding: 'clamp(8px, 2vw, 12px)', borderRadius: '6px', background: 'rgba(255, 0, 0, 0.1)' }}>
                  <p style={{ color: theme.text.primary, fontSize: 'clamp(13px, 2.5vw, 14px)', margin: 0, wordBreak: 'break-word' }}>Error: {error}</p>
                </div>
              )}
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: 'clamp(13px, 2.5vw, 14px)' }}>
                  Child Role *
                </label>
                <select
                  value={linkData.child_role}
                  onChange={(e) => setLinkData({ ...linkData, child_role: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '16px',
                    marginBottom: '12px',
                    minHeight: '44px',
                  }}
                >
                  <option value="">Select role</option>
                  <option value="son">👨 Son</option>
                  <option value="daughter">👩 Daughter</option>
                </select>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: theme.text.secondary, fontSize: 'clamp(14px, 3vw, 16px)', minHeight: '44px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={linkData.link_existing}
                    onChange={() => setLinkData({ ...linkData, link_existing: true })}
                    style={{ margin: 0, width: '20px', height: '20px', minWidth: '20px', minHeight: '20px', cursor: 'pointer' }}
                  />
                  Link to existing member
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: theme.text.secondary, fontSize: 'clamp(14px, 3vw, 16px)', minHeight: '44px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={!linkData.link_existing}
                    onChange={() => setLinkData({ ...linkData, link_existing: false })}
                    style={{ margin: 0, width: '20px', height: '20px', minWidth: '20px', minHeight: '20px', cursor: 'pointer' }}
                  />
                  Create new child
                </label>
              </div>

              {linkData.link_existing ? (
                <div style={{ marginBottom: '12px' }}>
                  <SearchablePersonSelect
                    value={linkData.related_person_id}
                    onChange={(personId) => setLinkData({ ...linkData, related_person_id: personId })}
                    options={familyMembers.filter((member) => member.id !== id)}
                    placeholder="Search for a child..."
                    excludeId={id}
                  />
                </div>
              ) : (
                <div className="relationship-form-grid">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '12px', marginBottom: '12px' }}>
                    <input
                      type="text"
                      placeholder="First Name *"
                      value={linkData.new_person.first_name}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, first_name: e.target.value } })}
                      required
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Surname (Maiden)"
                      value={linkData.new_person.last_name}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, last_name: e.target.value } })}
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '12px', marginBottom: '12px' }}>
                    <input
                      type="text"
                      placeholder="Preferred Name"
                      value={linkData.new_person.preferred_name}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, preferred_name: e.target.value } })}
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                    <input
                      type="date"
                      placeholder="Date of Birth"
                      value={linkData.new_person.date_of_birth}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, date_of_birth: e.target.value } })}
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  type="submit"
                  style={{
                    padding: '12px 20px',
                    borderRadius: '6px',
                    border: 'none',
                    background: theme.accent.primary,
                    color: 'white',
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: '44px',
                    flex: 1,
                    minWidth: '120px',
                  }}
                >
                  Add Child
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddChildForm(false);
                    setLinkData({ related_person_id: '', relationship_type: '', relationship_role: '', spouse_role: '', sibling_role: '', child_role: '', new_person: { first_name: '', last_name: '', preferred_name: '', date_of_birth: '' }, link_existing: true });
                  }}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: 'transparent',
                    color: theme.text.primary,
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: '44px',
                    flex: 1,
                    minWidth: '120px',
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Add Spouse Form */}
          {showAddPartnerForm && (
            <form 
              ref={addPartnerFormRef}
              onSubmit={handleAddPartner} 
              style={{ marginTop: '16px', paddingTop: '16px', borderTop: theme.glass.border, scrollMarginTop: '80px' }} 
              className="relationship-form"
            >
              {error && (
                <div style={{ marginBottom: '12px', padding: 'clamp(8px, 2vw, 12px)', borderRadius: '6px', background: 'rgba(255, 0, 0, 0.1)' }}>
                  <p style={{ color: theme.text.primary, fontSize: 'clamp(13px, 2.5vw, 14px)', margin: 0, wordBreak: 'break-word' }}>Error: {error}</p>
                </div>
              )}
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: 'clamp(13px, 2.5vw, 14px)' }}>
                  Spouse Role *
                </label>
                <select
                  value={linkData.spouse_role}
                  onChange={(e) => setLinkData({ ...linkData, spouse_role: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '16px',
                    marginBottom: '12px',
                    minHeight: '44px',
                  }}
                >
                  <option value="">Select role</option>
                  <option value="husband">👨 Husband</option>
                  <option value="wife">👩 Wife</option>
                </select>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: theme.text.secondary, fontSize: 'clamp(14px, 3vw, 16px)', minHeight: '44px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={linkData.link_existing}
                    onChange={() => setLinkData({ ...linkData, link_existing: true })}
                    style={{ margin: 0, width: '20px', height: '20px', minWidth: '20px', minHeight: '20px', cursor: 'pointer' }}
                  />
                  Link to existing member
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: theme.text.secondary, fontSize: 'clamp(14px, 3vw, 16px)', minHeight: '44px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={!linkData.link_existing}
                    onChange={() => setLinkData({ ...linkData, link_existing: false })}
                    style={{ margin: 0, width: '20px', height: '20px', minWidth: '20px', minHeight: '20px', cursor: 'pointer' }}
                  />
                  Create new spouse
                </label>
              </div>

              {linkData.link_existing ? (
                <div style={{ marginBottom: '12px' }}>
                  <SearchablePersonSelect
                    value={linkData.related_person_id}
                    onChange={(personId) => setLinkData({ ...linkData, related_person_id: personId })}
                    options={familyMembers.filter((member) => member.id !== id)}
                    placeholder="Search for a spouse..."
                    excludeId={id}
                  />
                </div>
              ) : (
                <div className="relationship-form-grid">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '12px', marginBottom: '12px' }}>
                    <input
                      type="text"
                      placeholder="First Name *"
                      value={linkData.new_person.first_name}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, first_name: e.target.value } })}
                      required
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Surname (Maiden)"
                      value={linkData.new_person.last_name}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, last_name: e.target.value } })}
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '12px', marginBottom: '12px' }}>
                    <input
                      type="text"
                      placeholder="Preferred Name"
                      value={linkData.new_person.preferred_name}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, preferred_name: e.target.value } })}
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                    <input
                      type="date"
                      placeholder="Date of Birth"
                      value={linkData.new_person.date_of_birth}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, date_of_birth: e.target.value } })}
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  type="submit"
                  style={{
                    padding: '12px 20px',
                    borderRadius: '6px',
                    border: 'none',
                    background: theme.accent.primary,
                    color: 'white',
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: '44px',
                    flex: 1,
                    minWidth: '120px',
                  }}
                >
                  Add Spouse
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddPartnerForm(false);
                    setLinkData({ related_person_id: '', relationship_type: '', relationship_role: '', spouse_role: '', sibling_role: '', child_role: '', new_person: { first_name: '', last_name: '', preferred_name: '', date_of_birth: '' }, link_existing: true });
                  }}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: 'transparent',
                    color: theme.text.primary,
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: '44px',
                    flex: 1,
                    minWidth: '120px',
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Add Sibling Form */}
          {showAddSiblingForm && (
            <form 
              ref={addSiblingFormRef}
              onSubmit={handleAddSibling} 
              style={{ marginTop: '16px', paddingTop: '16px', borderTop: theme.glass.border, scrollMarginTop: '80px' }} 
              className="relationship-form"
            >
              {error && (
                <div style={{ marginBottom: '12px', padding: 'clamp(8px, 2vw, 12px)', borderRadius: '6px', background: 'rgba(255, 0, 0, 0.1)' }}>
                  <p style={{ color: theme.text.primary, fontSize: 'clamp(13px, 2.5vw, 14px)', margin: 0, wordBreak: 'break-word' }}>Error: {error}</p>
                </div>
              )}
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: 'clamp(13px, 2.5vw, 14px)' }}>
                  Sibling Role *
                </label>
                <select
                  value={linkData.sibling_role}
                  onChange={(e) => setLinkData({ ...linkData, sibling_role: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '16px',
                    marginBottom: '12px',
                    minHeight: '44px',
                  }}
                >
                  <option value="">Select role</option>
                  <option value="brother">👨 Brother</option>
                  <option value="sister">👩 Sister</option>
                </select>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: theme.text.secondary, fontSize: 'clamp(14px, 3vw, 16px)', minHeight: '44px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={linkData.link_existing}
                    onChange={() => setLinkData({ ...linkData, link_existing: true })}
                    style={{ margin: 0, width: '20px', height: '20px', minWidth: '20px', minHeight: '20px', cursor: 'pointer' }}
                  />
                  Link to existing member
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: theme.text.secondary, fontSize: 'clamp(14px, 3vw, 16px)', minHeight: '44px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={!linkData.link_existing}
                    onChange={() => setLinkData({ ...linkData, link_existing: false })}
                    style={{ margin: 0, width: '20px', height: '20px', minWidth: '20px', minHeight: '20px', cursor: 'pointer' }}
                  />
                  Create new sibling
                </label>
              </div>

              {linkData.link_existing ? (
                <div style={{ marginBottom: '12px' }}>
                  <SearchablePersonSelect
                    value={linkData.related_person_id}
                    onChange={(personId) => setLinkData({ ...linkData, related_person_id: personId })}
                    options={familyMembers.filter((member) => member.id !== id)}
                    placeholder="Search for a sibling..."
                    excludeId={id}
                  />
                </div>
              ) : (
                <div className="relationship-form-grid">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '12px', marginBottom: '12px' }}>
                    <input
                      type="text"
                      placeholder="First Name *"
                      value={linkData.new_person.first_name}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, first_name: e.target.value } })}
                      required
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Surname (Maiden)"
                      value={linkData.new_person.last_name}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, last_name: e.target.value } })}
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '12px', marginBottom: '12px' }}>
                    <input
                      type="text"
                      placeholder="Preferred Name"
                      value={linkData.new_person.preferred_name}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, preferred_name: e.target.value } })}
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                    <input
                      type="date"
                      placeholder="Date of Birth"
                      value={linkData.new_person.date_of_birth}
                      onChange={(e) => setLinkData({ ...linkData, new_person: { ...linkData.new_person, date_of_birth: e.target.value } })}
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: theme.card.background,
                        color: theme.text.primary,
                        fontSize: '16px',
                        minHeight: '44px',
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  type="submit"
                  style={{
                    padding: '12px 20px',
                    borderRadius: '6px',
                    border: 'none',
                    background: theme.accent.primary,
                    color: 'white',
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: '44px',
                    flex: 1,
                    minWidth: '120px',
                  }}
                >
                  Add Sibling
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddSiblingForm(false);
                    setLinkData({ related_person_id: '', relationship_type: '', relationship_role: '', spouse_role: '', sibling_role: '', child_role: '', new_person: { first_name: '', last_name: '', preferred_name: '', date_of_birth: '' }, link_existing: true });
                  }}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: 'transparent',
                    color: theme.text.primary,
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: '44px',
                    flex: 1,
                    minWidth: '120px',
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </GlassCard>
      )}

      {/* Delete Button at Bottom - Admin Only */}
      {isAdmin && (
        <GlassCard style={{ 
          marginTop: '24px',
          padding: 'clamp(16px, 4vw, 20px)',
          border: `2px solid rgba(255, 0, 0, 0.3)`,
          background: 'rgba(255, 0, 0, 0.05)',
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            alignItems: 'stretch',
          }}>
            <div style={{
              fontSize: 'clamp(14px, 3vw, 16px)',
              color: theme.text.secondary,
              marginBottom: '8px',
            }}>
              <strong style={{ color: '#ff4444' }}>Danger Zone</strong>
            </div>
            <button
              onClick={async () => {
                if (!window.confirm(`Are you sure you want to delete ${person.preferred_name || person.first_name} ${person.last_name || ''}? This will also delete all their relationships. This action cannot be undone.`)) {
                  return;
                }
                try {
                  await peopleAPI.delete(id);
                  // Redirect to people page after deletion
                  window.location.href = '/people';
                } catch (err) {
                  setError(err.response?.data?.error || 'Failed to delete member');
                }
              }}
              style={{
                padding: '14px 20px',
                borderRadius: '8px',
                border: '2px solid #ff4444',
                background: 'rgba(255, 0, 0, 0.1)',
                color: '#ff4444',
                fontSize: 'clamp(15px, 3vw, 16px)',
                fontWeight: 600,
                cursor: 'pointer',
                minHeight: '44px',
                width: '100%',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 0, 0, 0.1)';
              }}
            >
              🗑️ Delete Member
            </button>
          </div>
        </GlassCard>
      )}
    </div>
  );
};

export default PersonDetailPage;

